import api from "@/shared/services/apiClient";
import { deleteAttachmentFromAll } from "@/shared/services/uploadService";
import type {
  AnnouncementAttachment,
  PollResponse,
  PollResults,
} from "@/features/announcements/types/feed";

/**
 * Normalise a company array that may contain plain strings or
 * `{ value, label }` / `{ company_id, company_name }` objects
 * into a plain `string[]` of labels (display names).
 */
const normalizeCompanyArray = (arr: unknown): string[] => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((item: unknown) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        const obj = item as Record<string, unknown>;
        // Prefer label/company_name for display; fall back to value/company_id
        return String(
          obj.label ?? obj.company_name ?? obj.value ?? obj.company_id ?? "",
        );
      }
      return "";
    })
    .filter(Boolean);
};

export const announcementService = {
  getAnnouncements: async (companyFilter?: string, management?: boolean) => {
    const response = await api.get("/feed/getAnnouncements", {
      params: { company_filter: companyFilter, management },
    });
    const data = response.data;
    // Normalise company arrays so downstream components always receive string[]
    if (Array.isArray(data)) {
      return data.map((a: any) => ({
        ...a,
        company: normalizeCompanyArray(a.company),
        target_companies: a.target_companies
          ? normalizeCompanyArray(a.target_companies)
          : undefined,
      }));
    }
    return data;
  },

  createAnnouncement: async (formData: any) => {
    const { author_name, created_at, row_id, business_unit, ...cleanData } =
      formData;
    const payload = {
      ...cleanData,
      content_text: cleanData.content,
      date_from:
        formData.date_from instanceof Date
          ? formData.date_from.toISOString().split("T")[0]
          : formData.date_from || null,
      date_to:
        formData.date_to instanceof Date
          ? formData.date_to.toISOString().split("T")[0]
          : formData.date_to === "" || !formData.date_to
            ? null
            : formData.date_to,
      active: formData.active ?? true,
      comments_on: formData.comments_on ?? true,
    };
    const response = await api.post("/feed/createAnnouncement", payload);
    return response.data;
  },

  updateAnnouncement: async (rowId: number, formData: any) => {
    if (!rowId) return;
    const { author_name, created_at, row_id, business_unit, ...cleanData } =
      formData;
    const payload = {
      ...cleanData,
      content_text: cleanData.content,
      date_from:
        formData.date_from instanceof Date
          ? formData.date_from.toISOString().split("T")[0]
          : formData.date_from || null,
      date_to:
        formData.date_to instanceof Date
          ? formData.date_to.toISOString().split("T")[0]
          : formData.date_to === "" || !formData.date_to
            ? null
            : formData.date_to,
      active: !!formData.active,
      comments_on: !!formData.comments_on,
    };
    const response = await api.put(
      `/feed/updateAnnouncement/${rowId}`,
      payload,
    );
    return response.data;
  },

  toggleReaction: async (rowId: number, emoji: string) => {
    if (!rowId) return;
    const response = await api.post(`/feed/${rowId}/react`, { emoji });
    return response.data;
  },

  respondToPoll: async (rowId: number, optionIds: string[], notes: Record<string, string> = {}): Promise<PollResponse> => {
    const response = await api.post(`/feed/${rowId}/poll/respond`, { option_ids: optionIds, notes });
    return response.data;
  },

  withdrawPollResponse: async (rowId: number): Promise<PollResponse> => {
    const response = await api.delete(`/feed/${rowId}/poll/respond`);
    return response.data;
  },

  setPollLock: async (rowId: number, locked: boolean) => {
    const response = await api.put(`/feed/${rowId}/poll/lock`, { locked });
    return response.data;
  },

  getPollResults: async (rowId: number): Promise<PollResults> => {
    const response = await api.get(`/feed/${rowId}/poll/results`);
    return response.data;
  },

  exportPollResults: async (rowId: number, lang: string): Promise<void> => {
    const response = await api.get(`/feed/${rowId}/poll/results/export`, {
      params: { lang },
      responseType: "blob",
    });
    const disposition = String(response.headers["content-disposition"] ?? "");
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
    const filename = encoded ? decodeURIComponent(encoded) : plain || `poll_${rowId}.csv`;
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  getCompaniesWithUsers: async () => {
    const response = await api.get("/feed/companies-with-users");
    return response.data;
  },

  getBatchesByCompany: async (companyId: string) => {
    const response = await api.get(`/feed/batches/${companyId}`);
    return response.data;
  },

  previewAudience: async (company: string[], batch_no?: string, country?: string[], sending_org?: string) => {
    const response = await api.post("/feed/preview-audience", {
      company,
      batch_no,
      country,
      sending_org,
    });
    return response.data;
  },

  getReactions: async (rowId: number) => {
    const response = await api.get(`/feed/reactions/${rowId}`);
    return response.data;
  },

  getPosters: async () => {
    const response = await api.get("/feed/posters");
    return response.data;
  },

  toggleFavorite: async (rowId: number) => {
    const response = await api.post(`/feed/${rowId}/favorite`);
    return response.data as { is_favorited: boolean };
  },

  markAsSeen: async (rowId: number) => {
    await api.post(`/feed/${rowId}/mark-seen`);
  },

  getViewers: async (rowId: number) => {
    const response = await api.get(`/feed/${rowId}/viewers`);
    const data = response.data;
    if (!Array.isArray(data)) return [];
    return data.map((item: Record<string, unknown>) => ({
      name: String(
        item.name ?? item.user_name ?? item.full_name ?? item.author_name ?? "",
      ),
      company: String(
        item.company ?? item.company_name ?? item.business_unit ?? "",
      ),
      seen_at: String(
        item.seen_at ??
          item.viewed_at ??
          item.created_at ??
          item.timestamp ??
          "",
      ),
    }));
  },

  getAnnouncementAttachments: async (
    announcementId: number,
  ): Promise<AnnouncementAttachment[]> => {
    const response = await api.get(
      `/attachments/announcements/${announcementId}`,
    );
    return response.data?.attachments ?? response.data ?? [];
  },

  renameAttachment: async (
    attachmentId: number | string,
    newName: string,
  ): Promise<void> => {
    await api.put(`/attachments/${attachmentId}/rename`, {
      display_name: newName,
    });
  },

  deleteAnnouncementAttachment: async (
    attachmentId: number | string,
  ): Promise<void> => {
    await deleteAttachmentFromAll(attachmentId);
  },

  deleteAnnouncement: async (rowId: number) => {
    if (!rowId) {
      throw new Error("Row ID is required to delete an announcement");
    }

    // Calls the endpoint: DELETE /feed/deleteAnnouncement/:rowId
    const response = await api.delete(`/feed/deleteAnnouncement/${rowId}`);
    return response.data;
  },
};
